"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

// API imports
import { getBookingInstate } from "@repo/ui/api/getBookingInstate";
import { removeSssrsFromBookingInState } from "@repo/ui/api/removeSssrsFromBookingInState";
import { sellSSRs } from "@repo/ui/api/sellSsrs";
// Constants
import type { Direction } from "@repo/ui/constants/directions";
import { SSR_CODES, SSR_CODES_CHECKIN } from "@repo/ui/constants/ssrs";
// Helpers
import { isSsrIncluded } from "@repo/ui/helpers/Extras/isSsrIncluded";
import { getTabSessionId } from "@repo/ui/lib/tabSessionId";
// Types
import type { BoardingPassSection, CommonInfoSection } from "@repo/ui/types/cms";
import type { Passenger } from "@repo/ui/types/manageBooking";

// Store
import { useAppDispatch, useAppSelector } from "@repo/store/hooks";
import {
  clearBoardingCard,
  selectBookingDetails,
  selectIsCheckIn,
  setBoardingPassSelection,
  setBookingDetails,
  setShowBoardingPassSelectError,
  useButtonStatus,
  useIsPostBooking,
  useShowBoardingPassSelectError,
} from "@repo/store/slices";

// Components
import FlightSegmentCard from "../common/FlightSegmentCard";
import ExtrasBoardingPassOptionItemCard from "./BoardingPassItemCard";

/**
 * Represents a selectable boarding pass option with pricing and metadata
 */
export type Option = {
  id: string;
  title: string;
  isIncluded?: boolean;
  inBundle?: boolean;
  price: number;
  ssrKey?: string;
};

/**
 * Props for the ExtrasBoardingPassOptionCard component
 */
type ExtrasBoardingPassOptionCardProps = {
  segmentId: string;
  direction: Direction;
  directionLabel?: string;
  origin: string;
  destination: string;
  options: Option[];
  inBundle?: boolean;
  bundleCode?: string;
  passengerKey?: string;
  InBundleInAnyJourney?: boolean;
  commonInfo?: CommonInfoSection;
  passengers?: Passenger[];
  cmsData?: BoardingPassSection;
  isJourneyDisabled?: boolean;
};

/**
 * Determines the appropriate SSR code based on checkout type.
 * Both check-in and post-booking (MMB) flows use the check-in variant of the code.
 */
function getBoardingPassSSRCode(isCheckin: boolean, isPostBooking: boolean): string {
  return isCheckin || isPostBooking ? SSR_CODES_CHECKIN.BOARDING_PASS : SSR_CODES.BOARDING_PASS;
}

/**
 * Determines the appropriate cart type based on checkout type
 */
function getCartType(isCheckin: boolean): "checkInCart" | "bookingCart" {
  return isCheckin ? "checkInCart" : "bookingCart";
}

/**
 * ExtrasBoardingPassOptionCard
 *
 * Manages selection and purchasing of boarding pass options for flight segments.
 * Handles both booking flow and check-in flows with SSR management.
 *
 * Key responsibilities:
 * - Display selectable boarding pass options with pricing
 * - Manage Redux state for selections
 * - Handle SSR (Special Service Request) addition/removal
 * - Validate selections in booking flow
 * - Disable options when segment is closed or already purchased
 */
export default function ExtrasBoardingPassOptionCard({
  segmentId,
  direction,
  directionLabel,
  options,
  inBundle = false,
  bundleCode,
  passengerKey,
  InBundleInAnyJourney,
  commonInfo,
  passengers,
  cmsData,
  isJourneyDisabled = false,
}: Readonly<ExtrasBoardingPassOptionCardProps>) {
  const dispatch = useAppDispatch();
  const buttonStatus = useButtonStatus();
  const isCheckin = useAppSelector(selectIsCheckIn);
  const bookingDetails = useAppSelector(selectBookingDetails);

  const isPostBooking = useIsPostBooking();
  const showBoardingPassSelectError = useShowBoardingPassSelectError();

  // Local state
  const [loadingOptionId, setLoadingOptionId] = useState<string | null>(null);

  // Derived state
  const isBookingFlow = !isCheckin && !isPostBooking;

  const selectedOptionId = useMemo(
    () =>
      buttonStatus?.boardingPassSelections?.[segmentId] ??
      (inBundle ? options[1]?.id : isCheckin || isPostBooking ? options[0]?.id : null),
    [buttonStatus?.boardingPassSelections, segmentId, inBundle, options, isCheckin, isPostBooking],
  );

  const bundlePrice = useMemo(() => options[1]?.price ?? 0, [options]);

  const hasIncludedSSR = useMemo(
    () =>
      isSsrIncluded(
        passengers,
        passengerKey,
        getBoardingPassSSRCode(isCheckin, isPostBooking),
        direction,
      ),
    [passengers, passengerKey, isCheckin, isPostBooking, direction],
  );

  /**
   * Returns true if the boarding pass SSR (APCP) is already present in
   * bookingDetails for the given passenger and journey.
   *
   * `segmentId` is a composite key built as `${passengerKey}-${journeyKey}`,
   * so we split on the first `-` occurrence to extract the journey key and
   * then search across all segments of that journey in the booking in-state.
   */
  const isBoardingPassSsrInBookingState = useMemo((): boolean => {
    if (!bookingDetails || !passengerKey) return false;

    const ssrCode = getBoardingPassSSRCode(isCheckin, isPostBooking);

    // segmentId format: "<passengerKey>-<journeyKey>"
    // journeyKey itself may contain "-", so strip only the passengerKey prefix.
    const journeyKey = segmentId.startsWith(`${passengerKey}-`)
      ? segmentId.slice(passengerKey.length + 1)
      : null;

    if (!journeyKey) return false;

    const journey = bookingDetails.journeys.find((j) => j.journeyKey === journeyKey);
    if (!journey) return false;

    return journey.segments.some((segment) => {
      const paxSegment = segment.passengerSegment?.[passengerKey];
      return paxSegment?.ssrs?.some((ssr) => ssr.ssrCode === ssrCode) ?? false;
    });
  }, [bookingDetails, passengerKey, segmentId, isCheckin, isPostBooking]);

  const isSelectionError = useMemo(
    () => isBookingFlow && showBoardingPassSelectError && !selectedOptionId,
    [isBookingFlow, showBoardingPassSelectError, selectedOptionId],
  );

  const isDisabled = inBundle || hasIncludedSSR || isJourneyDisabled;

  /**
   * Initialize bundle selection if applicable
   */
  useEffect(() => {
    if ((!inBundle && !hasIncludedSSR) || !passengerKey || !segmentId) return;

    const hasExistingSelection = buttonStatus?.boardingPassSelections?.[segmentId];
    if (hasExistingSelection) return;

    dispatch(
      setBoardingPassSelection({
        segmentId,
        passengerId: passengerKey,
        optionId: inBundle ? options[1]?.id || null : options[0]?.id || null,
        price: bundlePrice,
        isIncluded: true,
        direction,
        cartType: getCartType(isCheckin),
        ssrKey: "",
        ssrCode: getBoardingPassSSRCode(isCheckin, isPostBooking),
        alreadyAdded: true,
      }),
    );
  }, [
    inBundle,
    hasIncludedSSR,
    options,
    segmentId,
    passengerKey,
    direction,
    bundlePrice,
    buttonStatus?.boardingPassSelections,
    dispatch,
    isCheckin,
    isPostBooking,
  ]);

  /**
   * Handles SSR operations (addition or removal)
   */
  const manageSsrs = useCallback(
    async (
      operation: "add" | "remove",
      ssrKey: string | undefined,
      tabSessionId: string,
    ): Promise<void> => {
      if (!ssrKey) return;

      try {
        if (operation === "add") {
          const currencyCode = bookingDetails?.currencyCode ?? "CLP";
          await sellSSRs([ssrKey], currencyCode, tabSessionId);
        } else {
          await removeSssrsFromBookingInState({ ssrKeys: [ssrKey] }, tabSessionId);
        }
      } catch (error) {
        console.error(`Error ${operation}ing SSR:`, error);
        // Re-throw to allow caller to handle
        throw error;
      }
    },
    [bookingDetails?.currencyCode],
  );

  /**
   * Refreshes booking details from server
   */
  const refreshBookingDetails = useCallback(
    async (tabSessionId: string): Promise<void> => {
      try {
        const booking = await getBookingInstate(tabSessionId);
        if (booking) {
          dispatch(setBookingDetails(booking));
        }
      } catch (error) {
        console.error("Error refreshing booking details:", error);
      }
    },
    [dispatch],
  );

  /**
   * Dispatches Redux action to update boarding pass selection
   */
  const updateSelection = useCallback(
    (
      optionId: string,
      passengerId: string | undefined,
      price: number,
      isIncluded: boolean,
      ssrKey: string | undefined,
    ): void => {
      dispatch(setShowBoardingPassSelectError(false));
      dispatch(
        setBoardingPassSelection({
          segmentId,
          optionId,
          price,
          isIncluded,
          direction,
          passengerId,
          cartType: getCartType(isCheckin),
          ssrKey: ssrKey || "",
          ssrCode: getBoardingPassSSRCode(isCheckin, isPostBooking),
        }),
      );
    },
    [dispatch, segmentId, direction, isCheckin, isPostBooking],
  );

  /**
   * Clears the boarding pass selection
   */
  const clearSelection = useCallback(
    (passengerId: string | undefined): void => {
      if (!passengerId) return;

      dispatch(
        clearBoardingCard({
          direction,
          passengerKey: passengerId,
          segmentId,
          cartType: getCartType(isCheckin),
        }),
      );
    },
    [dispatch, direction, segmentId, isCheckin],
  );

  /**
   * Main handler for option change event
   *
   * Logic flow:
   * 1. Validate change is allowed
   * 2. Update Redux state
   * 3. Manage SSRs (add/remove as needed)
   * 4. Refresh booking details
   */
  const handleOptionChange = useCallback(
    async (
      checked: boolean,
      optionId: string,
      passengerId: string | undefined,
      ssrKey: string | undefined,
    ): Promise<void> => {
      // Guard clauses
      if (inBundle || isDisabled) return;

      const selectedOption = options.find((opt) => opt.id === optionId);
      const isFirstOption = optionId === options[0]?.id;
      const isCurrentlySelected = selectedOptionId?.toLowerCase() === optionId.toLowerCase();

      if (isCurrentlySelected) return;

      try {
        setLoadingOptionId(optionId);
        const tabSessionId = getTabSessionId();

        if (!tabSessionId) {
          console.error("No tab session ID found");
          return;
        }

        // Track whether an SSR API call was actually made
        let ssrApiCalled = false;

        // Handle unchecking
        if (checked) {
          // Handle checking
          const price = selectedOption?.price ?? 0;
          const isIncluded = isFirstOption || selectedOption?.isIncluded === true;

          // Update state
          updateSelection(optionId, passengerId, isFirstOption ? 0 : price, isIncluded, ssrKey);

          // Clear previous selection if selecting first option
          if (isFirstOption && passengerId) {
            clearSelection(passengerId);
            // Only call remove API if the boarding pass SSR is already present
            // in the booking in-state for this specific passenger and journey.
            if (isBoardingPassSsrInBookingState) {
              const paidOptionSSRKey = options[1]?.ssrKey;
              await manageSsrs("remove", paidOptionSSRKey, tabSessionId);
              ssrApiCalled = true;
            }
          } else if (ssrKey) {
            // Add SSR for paid option
            await manageSsrs("add", ssrKey, tabSessionId);
            ssrApiCalled = true;
          }
        } else {
          clearSelection(passengerId);
          // Remove SSR if unchecking the paid option
          if (!isFirstOption && ssrKey) {
            await manageSsrs("remove", ssrKey, tabSessionId);
            ssrApiCalled = true;
          }
        }

        // Only refresh booking details when an SSR API call was made
        if (ssrApiCalled) {
          await refreshBookingDetails(tabSessionId);
        }
      } catch (error) {
        console.error("Error handling option change:", error);
        // Optionally dispatch error state to Redux for UI feedback
      } finally {
        setLoadingOptionId(null);
      }
    },
    [
      inBundle,
      isDisabled,
      options,
      selectedOptionId,
      isBoardingPassSsrInBookingState,
      clearSelection,
      updateSelection,
      manageSsrs,
      refreshBookingDetails,
    ],
  );

  return (
    <FlightSegmentCard
      bundleCode={bundleCode}
      direction={direction}
      directionLabel={directionLabel}
      disabled={isDisabled}
      InBundleInAnyJourney={InBundleInAnyJourney}
      cmsData={cmsData}
      isError={isSelectionError}
      commonInfo={commonInfo}
    >
      {options.map((option) => (
        <ExtrasBoardingPassOptionItemCard
          key={`${segmentId}-${option.id}`}
          id={option.id}
          title={option.title}
          inBundle={inBundle}
          price={option.price}
          checked={selectedOptionId === option.id}
          onChangeAction={(checked, ssrKey) =>
            handleOptionChange(checked, option.id, passengerKey, ssrKey)
          }
          disabled={isDisabled}
          commonInfo={commonInfo}
          ssrKey={option.ssrKey}
          loading={loadingOptionId === option.id}
        />
      ))}
    </FlightSegmentCard>
  );
}
